import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      // Only redirect if we're not already on login page to avoid loops
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password, totpToken = null) => {
    const response = await api.post('/api/auth/login', { email, password, totpToken });
    return response.data;
  },

  register: async (token, password) => {
    const response = await api.post('/api/auth/register', { token, password });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  invite: async (email) => {
    const response = await api.post('/api/auth/invite', { email });
    return response.data;
  },
};

// TOTP API
export const totpAPI = {
  setup: async () => {
    const response = await api.post('/api/totp/setup');
    return response.data;
  },

  enable: async (secret, token) => {
    const response = await api.post('/api/totp/enable', { secret, token });
    return response.data;
  },

  disable: async (token) => {
    const response = await api.post('/api/totp/disable', { token });
    return response.data;
  },
};

// Customers API
export const customersAPI = {
  getAll: async () => {
    const response = await api.get('/api/customers');
    return response.data;
  },

  getHistory: async (customerId, limit = 12) => {
    const response = await api.get(`/api/customers/${customerId}/history?limit=${limit}`);
    return response.data;
  },

  getGrowth: async (customerId) => {
    const response = await api.get(`/api/customers/${customerId}/growth`);
    return response.data;
  },
};

// Snapshots API
export const snapshotsAPI = {
  getLatest: async () => {
    const response = await api.get('/api/snapshots/latest');
    return response.data;
  },

  getDates: async () => {
    const response = await api.get('/api/snapshots/dates');
    return response.data;
  },

  getByDate: async (date) => {
    const response = await api.get(`/api/snapshots/date/${date}`);
    return response.data;
  },

  create: async (date = null) => {
    const response = await api.post('/api/snapshots/create', { date });
    return response.data;
  },
};

// Stats API
export const statsAPI = {
  getStats: async () => {
    const response = await api.get('/api/stats');
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },
};

// Public Data API (no auth required)
export const dataAPI = {
  getCustomerData: async (customerName) => {
    const response = await axios.get(`${API_BASE_URL}/data/${customerName}`);
    return response.data;
  },
};

// Export default with all APIs
export default {
  login: authAPI.login,
  register: authAPI.register,
  getCurrentUser: authAPI.getCurrentUser,
  invite: authAPI.invite,
  
  // TOTP
  setupTOTP: totpAPI.setup,
  enableTOTP: totpAPI.enable,
  disableTOTP: totpAPI.disable,
  
  // Customers
  getCustomers: customersAPI.getAll,
  getCustomerHistory: customersAPI.getHistory,
  getCustomerGrowth: customersAPI.getGrowth,
  
  // Snapshots
  getLatestSnapshots: snapshotsAPI.getLatest,
  getSnapshotDates: snapshotsAPI.getDates,
  getSnapshotsByDate: snapshotsAPI.getByDate,
  createSnapshot: snapshotsAPI.create,
  
  // Stats
  getStats: statsAPI.getStats,
  
  // Users
  getUsers: usersAPI.getAll,
  
  // Public Data
  getCustomerData: dataAPI.getCustomerData,
};

